(() => {
  const PERIOD_ORDER = ['today', 'tomorrow', 'weekly', 'monthly', 'love', 'yearly'];
  const SIGN_ORDER = ['aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo', 'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces'];
  const PERIOD_ALIASES = {
    today: 'today', daily: 'today',
    tomorrow: 'tomorrow',
    weekly: 'weekly', week: 'weekly', 'this-week': 'weekly',
    monthly: 'monthly', month: 'monthly', 'this-month': 'monthly',
    love: 'love', relationship: 'love', relationships: 'love',
    yearly: 'yearly', annual: 'yearly', year: 'yearly'
  };
  const DEFAULT_LIFE_QUESTIONS = [
    'Which career path best matches my natural strengths?',
    'When is the right time to make an important decision?',
    'How can I improve stability in my finances?',
    'What does my birth chart reveal about relationships?',
    'Which habits support my health and wellbeing?',
    'What is the most favourable time to begin something new?',
    'How can I move through a difficult professional phase?',
    'Which opportunities deserve my attention right now?',
    'What can I learn from repeating patterns in my life?',
    'How can I build stronger family relationships?',
    'What does my chart say about travel and relocation?',
    'Which spiritual practice is most supportive for me?',
    'How can I use my natural talents more confidently?',
    'What should I be cautious about in the coming months?',
    'Where is my greatest potential for personal growth?'
  ];
  const DEFAULT_LIFE_ANSWERS = [
    'Choose work that rewards your natural strengths, gives you meaningful responsibility and leaves room to keep learning. The most sustainable path is the one where confidence and consistency can grow together.',
    'Make the decision after urgency has settled. Gather the essential facts, notice what remains true after a quiet pause, and choose the option that supports your longer-term stability.',
    'Build financial stability through one clear budget, a dependable savings habit and fewer impulsive commitments. Small repeatable choices will serve you better than a dramatic short-term change.',
    'Your chart encourages direct but considerate communication. Relationships deepen when expectations are spoken early, affection is shown consistently and independence is balanced with genuine presence.',
    'Simple routines will support you best: steady sleep, regular movement, nourishing meals and time away from mental noise. Consistency matters more than intensity.',
    'Begin when the purpose is clear, the first practical step is ready and you can maintain momentum after the initial excitement. Good timing should feel grounded, not rushed.',
    'Treat this phase as a recalibration rather than a verdict. Focus on one useful skill, protect your energy and let measured progress rebuild professional confidence.',
    'Give attention to opportunities that match your values, use your proven abilities and still offer room to grow. Excitement is useful, but alignment and follow-through are stronger signals.',
    'Repeating patterns often point to a boundary, belief or unmet need asking to be understood. Notice the trigger, your usual response and the one different choice that could change the cycle.',
    'Stronger family bonds come from listening without immediately solving, naming needs respectfully and creating dependable moments of connection. Repair small misunderstandings before they become distance.',
    'Travel or relocation is most supportive when it has a clear purpose and practical foundation. Research the everyday realities as carefully as the exciting possibilities before committing.',
    'Choose a practice you can return to regularly, such as meditation, prayer, journaling or service. A simple ritual performed sincerely will be more supportive than a complicated one performed rarely.',
    'Use your talents in visible, manageable steps. Share one idea, accept one suitable responsibility and let evidence from completed work strengthen your confidence.',
    'Be cautious of rushed promises, avoidable expenses and decisions made only to relieve temporary pressure. Leave space to review details before making an important commitment.',
    'Your greatest growth lies where natural ability meets a healthy challenge. Move toward experiences that require courage and self-awareness without asking you to abandon your core values.'
  ];

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const clampRating = (value) => Math.max(1, Math.min(5, Number(value) || 1));

  const zodiacGlyph = (value = '') => `${String(value).replace(/\uFE0E|\uFE0F/g, '')}\uFE0E`;

  const normalizeKey = (value = '') => String(value)
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const canonicalSign = (...values) => {
    for (const value of values) {
      const normalized = normalizeKey(value);
      const match = SIGN_ORDER.find((key) => normalized === key || normalized.startsWith(`${key}-`) || normalized.endsWith(`-${key}`));
      if (match) return match;
    }
    return '';
  };

  const canonicalPeriod = (value) => PERIOD_ALIASES[normalizeKey(value)] || '';

  const hasValue = (value) => value !== undefined && value !== null && (typeof value !== 'string' || value.trim() !== '');

  const parseJsonValue = (value) => {
    let parsed = value;
    for (let index = 0; index < 2 && typeof parsed === 'string'; index += 1) {
      parsed = JSON.parse(parsed);
    }
    return parsed;
  };

  const formatDate = (date, options) => new Intl.DateTimeFormat('en-IN', options).format(date);

  const startOfWeek = (date) => {
    const copy = new Date(date);
    const day = copy.getDay() || 7;
    copy.setDate(copy.getDate() - day + 1);
    copy.setHours(0, 0, 0, 0);
    return copy;
  };

  const periodDateLabel = (period) => {
    const today = new Date();
    if (period === 'tomorrow') {
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      return formatDate(tomorrow, { day: 'numeric', month: 'long', year: 'numeric' });
    }
    if (period === 'weekly') {
      const start = startOfWeek(today);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return `${formatDate(start, { day: 'numeric', month: 'short' })} – ${formatDate(end, { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    if (period === 'monthly') {
      return formatDate(today, { month: 'long', year: 'numeric' });
    }
    if (period === 'yearly') {
      return String(today.getFullYear());
    }
    if (period === 'love') {
      return `Love guidance · ${formatDate(today, { month: 'long', year: 'numeric' })}`;
    }
    return formatDate(today, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const starMarkup = (rating, label = '') => {
    const score = clampRating(rating);
    return `<span class="horoscope-rating" aria-label="${escapeHtml(label || `${score} out of 5 stars`)}">${Array.from({ length: 5 }, (_, index) => `<span class="${index < score ? '' : 'horoscope-rating__empty'}" aria-hidden="true">★</span>`).join('')}</span>`;
  };

  const signSeason = (signs) => {
    const now = new Date();
    const month = now.getMonth() + 1;
    const day = now.getDate();
    const ranges = [
      ['capricorn', 12, 22, 1, 19], ['aquarius', 1, 20, 2, 18], ['pisces', 2, 19, 3, 20],
      ['aries', 3, 21, 4, 19], ['taurus', 4, 20, 5, 20], ['gemini', 5, 21, 6, 20],
      ['cancer', 6, 21, 7, 22], ['leo', 7, 23, 8, 22], ['virgo', 8, 23, 9, 22],
      ['libra', 9, 23, 10, 22], ['scorpio', 10, 23, 11, 21], ['sagittarius', 11, 22, 12, 21]
    ];
    const active = ranges.find(([, startMonth, startDay, endMonth, endDay]) => {
      if (startMonth > endMonth) return (month === startMonth && day >= startDay) || (month === endMonth && day <= endDay);
      return (month === startMonth && day >= startDay) || (month === endMonth && day <= endDay);
    });
    return signs.find((sign) => sign.slug === active?.[0]) || signs[0];
  };

  class HoroscopeExperience extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.loading = this.querySelector('[data-horoscope-loading]');
      this.error = this.querySelector('[data-horoscope-error]');
      this.app = this.querySelector('[data-horoscope-app]');
      this.periodNav = this.querySelector('[data-horoscope-periods]');
      this.view = this.querySelector('[data-horoscope-view]');
      this.retryButton = this.querySelector('[data-horoscope-retry]');
      this.onClick = this.handleClick.bind(this);
      this.onPopState = this.render.bind(this);
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopState);
      this.retryButton?.addEventListener('click', () => this.load());
      this.load();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('popstate', this.onPopState);
    }

    async fetchJson(url, isRemote = false) {
      if (!url) throw new Error('A data URL was not configured.');
      const response = await fetch(url, {
        credentials: isRemote ? 'omit' : 'same-origin',
        cache: isRemote ? 'no-cache' : 'force-cache',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(`Data request failed with ${response.status}.`);
      return response.json();
    }

    embeddedData() {
      const node = this.querySelector('[data-horoscope-backend]');
      if (!node?.textContent.trim()) return null;
      return parseJsonValue(node.textContent.trim());
    }

    productData() {
      const node = this.querySelector('[data-horoscope-products]');
      if (!node?.textContent.trim()) return { products: [] };
      try {
        const value = parseJsonValue(node.textContent.trim());
        return value && Array.isArray(value.products) ? value : { products: [] };
      } catch (error) {
        if (window.Shopify?.designMode) console.error('[Horoscope experience] Invalid product data', error);
        return { products: [] };
      }
    }

    mergeProductData(data) {
      const products = this.productData().products;
      products.forEach((product) => {
        const slug = canonicalSign(product.slug, product.handle, product.name);
        const sign = data.signs.find((entry) => entry.slug === slug);
        if (!sign) return;

        if (hasValue(product.url)) sign.productUrl = product.url;
        if (hasValue(product.image)) sign.productImage = product.image;
        if (hasValue(product.productId)) sign.productId = product.productId;
        sign.readings = sign.readings || {};

        let experience = product.experience;
        if (typeof experience === 'string' && experience.trim()) {
          try { experience = parseJsonValue(experience); } catch (error) { experience = null; }
        }
        if (experience && typeof experience === 'object' && !Array.isArray(experience)) {
          [
            'symbol', 'dates', 'element', 'ruler', 'archetype', 'color', 'luckyNumber', 'bestDay',
            'strength', 'growth', 'career', 'health', 'love', 'finance', 'remedy', 'mantra',
            'lifeQuestions', 'compatibility', 'compatibilityScores'
          ].forEach((key) => {
            if (hasValue(experience[key])) sign[key] = experience[key];
          });
          if (experience.readings && typeof experience.readings === 'object') {
            Object.entries(experience.readings).forEach(([key, value]) => {
              const period = canonicalPeriod(key);
              if (period && value && typeof value === 'object') {
                sign.readings[period] = { ...(sign.readings[period] || {}), ...value };
              }
            });
          }
        }

        (Array.isArray(product.readings) ? product.readings : []).forEach((entry) => {
          if (entry?.enabled === false) return;
          const period = canonicalPeriod(entry?.periodKey);
          if (!period) return;
          const reading = {};
          ['headline', 'summary', 'intro', 'score', 'mood', 'focus', 'challenge', 'career', 'health', 'love', 'finance', 'remedy', 'mantra'].forEach((key) => {
            if (hasValue(entry[key])) reading[key] = entry[key];
          });
          sign.readings[period] = { ...(sign.readings[period] || {}), ...reading };
        });
      });
      return data;
    }

    validateData(data) {
      if (!data || typeof data !== 'object') throw new Error('Horoscope data must be a JSON object.');
      if (!data.periods || !Array.isArray(data.signs)) throw new Error('Horoscope data is missing periods or signs.');
      if (data.signs.length < 12) throw new Error('Horoscope data must include all 12 zodiac signs.');
      PERIOD_ORDER.forEach((period) => {
        if (!data.periods[period]) throw new Error(`Horoscope data is missing the ${period} period.`);
      });
      return data;
    }

    async resolveData() {
      const priority = this.dataset.sourcePriority || 'metaobject';
      const embedded = () => Promise.resolve(this.embeddedData()).then((value) => {
        if (!value) throw new Error('The configured metaobject record is empty.');
        return value;
      });
      const remote = () => this.fetchJson(this.dataset.remoteUrl, true);
      const bundled = () => this.fetchJson(this.dataset.fallbackUrl, false);
      const sources = priority === 'remote'
        ? [remote, embedded, bundled]
        : priority === 'bundled'
          ? [bundled]
          : [embedded, remote, bundled];
      let latestError;
      for (const source of sources) {
        try {
          const result = await source();
          return this.validateData(parseJsonValue(result));
        } catch (error) {
          latestError = error;
        }
      }
      throw latestError || new Error('No horoscope data source was available.');
    }

    async load() {
      this.loading.hidden = false;
      this.error.hidden = true;
      this.app.hidden = true;
      try {
        this.data = this.mergeProductData(await this.resolveData());
        this.loading.hidden = true;
        this.app.hidden = false;
        this.render();
      } catch (error) {
        this.loading.hidden = true;
        this.error.hidden = false;
        if (window.Shopify?.designMode) console.error('[Horoscope experience]', error);
      }
    }

    state() {
      const params = new URLSearchParams(window.location.search);
      const handle = (this.dataset.pageHandle || '').toLowerCase();
      const handleMatch = handle.match(/^horoscope(?:-(today|tomorrow|weekly|monthly|love|yearly))?(?:-([a-z]+))?$/);
      const period = params.get('period') || handleMatch?.[1] || this.dataset.defaultPeriod || 'today';
      const sign = canonicalSign(params.get('sign'), handleMatch?.[2], this.dataset.currentSign);
      return {
        period: this.data.periods[period] ? period : 'today',
        sign: this.data.signs.some((entry) => entry.slug === sign) ? sign : ''
      };
    }

    url(period, sign = '') {
      const targetSign = sign ? this.data?.signs?.find((entry) => entry.slug === sign) : null;
      const route = targetSign?.productUrl || this.dataset.baseRoute || window.location.pathname;
      const url = new URL(route, window.location.origin);
      url.searchParams.set('period', period);
      if (sign) url.searchParams.set('sign', sign);
      return `${url.pathname}${url.search}${url.hash}`;
    }

    handleClick(event) {
      const questionCard = event.target.closest('[data-horoscope-question]');
      if (questionCard) {
        const isFlipped = questionCard.classList.toggle('is-flipped');
        questionCard.setAttribute('aria-pressed', String(isFlipped));
        questionCard.setAttribute('aria-label', isFlipped
          ? `${questionCard.dataset.answerLabel}. Show question.`
          : `Reveal answer for: ${questionCard.dataset.questionLabel}`);
        return;
      }

      const link = event.target.closest('a[data-horoscope-link]');
      if (!link || this.dataset.enableHistory !== 'true') return;
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const url = new URL(link.href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      event.preventDefault();
      window.history.pushState({}, '', `${url.pathname}${url.search}${url.hash}`);
      this.render();
      window.requestAnimationFrame(() => {
        const top = this.getBoundingClientRect().top + window.scrollY;
        if (window.scrollY > top) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.view.focus({ preventScroll: true });
      });
    }

    renderPeriods(activePeriod, activeSign = '') {
      this.periodNav.innerHTML = PERIOD_ORDER.map((key) => {
        const period = this.data.periods[key];
        return `<a href="${escapeHtml(this.url(key, activeSign))}" data-horoscope-link${key === activePeriod ? ' aria-current="page"' : ''}>${escapeHtml(period.label)}</a>`;
      }).join('');
    }

    reading(periodKey, sign, index) {
      const period = this.data.periods[periodKey];
      const score = clampRating(period.scores?.[index]);
      const focus = period.focus?.[index] || sign.strength;
      const challenge = period.challenge?.[index] || sign.growth;
      const mood = period.mood?.[index] || sign.archetype;
      const generatedSummary = `${sign.name} is encouraged to prioritise ${focus}. ${sign.strength} is an advantage, while ${challenge} needs conscious attention.`;
      const override = sign.readings?.[periodKey] || {};
      return {
        score: clampRating(hasValue(override.score) ? override.score : score),
        focus: hasValue(override.focus) ? override.focus : focus,
        challenge: hasValue(override.challenge) ? override.challenge : challenge,
        mood: hasValue(override.mood) ? override.mood : mood,
        summary: hasValue(override.summary) ? override.summary : generatedSummary,
        headline: hasValue(override.headline) ? override.headline : '',
        intro: hasValue(override.intro) ? override.intro : '',
        career: hasValue(override.career) ? override.career : sign.career,
        health: hasValue(override.health) ? override.health : sign.health,
        love: hasValue(override.love) ? override.love : sign.love,
        finance: hasValue(override.finance) ? override.finance : sign.finance,
        remedy: hasValue(override.remedy) ? override.remedy : sign.remedy,
        mantra: hasValue(override.mantra) ? override.mantra : sign.mantra
      };
    }

    cardFacts(periodKey, sign, reading) {
      if (periodKey === 'weekly') return [['Element', sign.element], ['Best day', sign.bestDay]];
      if (periodKey === 'monthly') return [['Monthly theme', reading.focus], ['Ruling planet', sign.ruler]];
      if (periodKey === 'love') {
        const match = this.data.signs.find((entry) => entry.slug === sign.compatibility?.[0]);
        return [['Love tone', reading.mood], ['Strong match', match?.name || '—']];
      }
      if (periodKey === 'yearly') return [['Element', sign.element], ['Year theme', reading.focus]];
      return [['Element', sign.element], ['Lucky number', String(sign.luckyNumber)]];
    }

    renderCard(sign, index, periodKey) {
      const reading = this.reading(periodKey, sign, index);
      const facts = this.cardFacts(periodKey, sign, reading);
      return `
        <a class="horoscope-card" href="${escapeHtml(this.url(periodKey, sign.slug))}" data-horoscope-link aria-label="Read ${escapeHtml(sign.name)} ${escapeHtml(this.data.periods[periodKey].label.toLowerCase())} horoscope">
          <div class="horoscope-card__top">
            <span class="horoscope-card__symbol" aria-hidden="true">${escapeHtml(sign.symbol)}</span>
            <span>
              <h2>${escapeHtml(sign.name)}</h2>
              <span class="horoscope-card__dates">${escapeHtml(sign.dates)}</span>
            </span>
          </div>
          <div class="horoscope-card__rating">${starMarkup(reading.score)}</div>
          <p class="horoscope-card__summary">${escapeHtml(reading.summary)}</p>
          <div class="horoscope-card__facts">
            ${facts.map(([label, value]) => `<span class="horoscope-card__fact"><small>${escapeHtml(label)}</small><strong title="${escapeHtml(value)}">${escapeHtml(value)}</strong></span>`).join('')}
          </div>
          <span class="horoscope-card__cta"><span>Read horoscope</span><span aria-hidden="true">→</span></span>
        </a>`;
    }

    renderFaq() {
      if (this.dataset.showFaq !== 'true' || !Array.isArray(this.data.faq)) return '';
      return `
        <section aria-labelledby="HoroscopeFaq-${escapeHtml(this.dataset.sectionId)}">
          <header class="horoscope-section-heading">
            <h2 id="HoroscopeFaq-${escapeHtml(this.dataset.sectionId)}">Frequently Asked Questions</h2>
            <p>Clear guidance about how to use these readings.</p>
          </header>
          <div class="horoscope-faq">
            ${this.data.faq.map((item, index) => `<details${index === 0 ? ' open' : ''}><summary>${escapeHtml(item.question)}</summary><p>${escapeHtml(item.answer)}</p></details>`).join('')}
          </div>
        </section>`;
    }

    disclaimer() {
      if (this.dataset.showDisclaimer !== 'true' || !this.dataset.disclaimer) return '';
      return `<p class="horoscope-disclaimer">${escapeHtml(this.dataset.disclaimer)}</p>`;
    }

    renderOverview(periodKey) {
      const period = this.data.periods[periodKey];
      const seasonalSign = signSeason(this.data.signs);
      const alternatePeriods = PERIOD_ORDER.filter((key) => key !== periodKey).slice(0, 3);
      return `
        <section class="horoscope-overview">
          <header class="horoscope-hero">
            <span class="horoscope-eyebrow">Western astrology · Sun sign</span>
            <span class="horoscope-hero__symbol" aria-hidden="true">${escapeHtml(seasonalSign.symbol)}</span>
            <h1>${escapeHtml(period.title)}</h1>
            <p class="horoscope-hero__description">${escapeHtml(period.description)}</p>
            <span class="horoscope-hero__meta">Updated for ${escapeHtml(periodDateLabel(periodKey))}</span>
          </header>

          <div class="horoscope-grid" aria-label="${escapeHtml(period.label)} horoscope signs">
            ${this.data.signs.map((sign, index) => this.renderCard(sign, index, periodKey)).join('')}
          </div>

          <section aria-labelledby="HoroscopeMore-${escapeHtml(this.dataset.sectionId)}">
            <header class="horoscope-section-heading">
              <h2 id="HoroscopeMore-${escapeHtml(this.dataset.sectionId)}">More Ways to Read Your Stars</h2>
              <p>Switch the time period while keeping all twelve signs in view.</p>
            </header>
            <div class="horoscope-period-cards">
              ${alternatePeriods.map((key) => `<a class="horoscope-period-card" href="${escapeHtml(this.url(key))}" data-horoscope-link><strong>${escapeHtml(this.data.periods[key].label)}</strong><span>${escapeHtml(this.data.periods[key].description)}</span></a>`).join('')}
            </div>
          </section>

          ${this.renderFaq()}
          ${this.disclaimer()}
        </section>`;
    }

    areaRating(score, offset) {
      return clampRating(score + offset);
    }

    medallion(sign, className = '') {
      const classes = `horoscope-medallion${className ? ` ${className}` : ''}`;
      if (sign.productImage) {
        return `<span class="${classes}"><img src="${escapeHtml(sign.productImage)}" alt="${escapeHtml(sign.name)}" width="280" height="280" loading="eager" fetchpriority="high"></span>`;
      }
      return `<span class="${classes}" aria-label="${escapeHtml(sign.name)}"><span aria-hidden="true">${escapeHtml(zodiacGlyph(sign.symbol))}</span></span>`;
    }

    tone(reading) {
      if (reading.score >= 4) return 'Positive';
      if (reading.score <= 2) return 'Reflective';
      return 'Mixed';
    }

    compatibilityScore(sign, match, signIndex, matchIndex) {
      const stored = sign.compatibilityScores;
      if (stored && !Array.isArray(stored) && hasValue(stored[match.slug])) {
        return Math.max(1, Math.min(100, Number(stored[match.slug]) || 1));
      }
      if (Array.isArray(stored) && hasValue(stored[matchIndex])) {
        return Math.max(1, Math.min(100, Number(stored[matchIndex]) || 1));
      }
      if (sign.slug === match.slug) return 80;
      const favouriteIndex = (sign.compatibility || []).indexOf(match.slug);
      if (favouriteIndex > -1) return [95, 88, 82][favouriteIndex] || 80;
      return 48 + ((signIndex * 13 + matchIndex * 9) % 31);
    }

    renderLifeQuestions(sign) {
      if (this.dataset.showLifeQuestions !== 'true') return '';
      const sourceQuestions = Array.isArray(sign.lifeQuestions) && sign.lifeQuestions.length
        ? sign.lifeQuestions
        : DEFAULT_LIFE_QUESTIONS;
      const questions = sourceQuestions.slice(0, 16).map((item, index) => {
        const isObject = item && typeof item === 'object' && !Array.isArray(item);
        return {
          question: isObject ? (item.question || item.title || DEFAULT_LIFE_QUESTIONS[index] || `Life question ${index + 1}`) : item,
          answer: isObject ? (item.answer || item.content || item.description || DEFAULT_LIFE_ANSWERS[index % DEFAULT_LIFE_ANSWERS.length]) : DEFAULT_LIFE_ANSWERS[index % DEFAULT_LIFE_ANSWERS.length]
        };
      }).filter((item) => hasValue(item.question));
      return `
        <section class="horoscope-life-questions horoscope-ornament" aria-labelledby="HoroscopeQuestions-${escapeHtml(this.dataset.sectionId)}">
          <header class="horoscope-showcase-heading">
            <span class="horoscope-showcase-heading__icon" aria-hidden="true">☼</span>
            <h2 id="HoroscopeQuestions-${escapeHtml(this.dataset.sectionId)}">Explore Your Life Questions</h2>
            <p>Discover clarity and direction through astrology. Explore thoughtful prompts for your important life questions through ${escapeHtml(sign.name)} guidance.</p>
          </header>
          <div class="horoscope-question-grid">
            ${questions.map(({ question, answer }, index) => `
              <button type="button" class="horoscope-question-card horoscope-question-card--${(index % 4) + 1}" data-horoscope-question data-question-label="${escapeHtml(question)}" data-answer-label="${escapeHtml(answer)}" aria-pressed="false" aria-label="Reveal answer for: ${escapeHtml(question)}">
                <span class="horoscope-question-card__inner">
                  <span class="horoscope-question-card__face horoscope-question-card__front">
                    <small>Question ${String(index + 1).padStart(2, '0')}</small>
                    <strong>${escapeHtml(question)}</strong>
                    <i>View answer <span aria-hidden="true">→</span></i>
                  </span>
                  <span class="horoscope-question-card__face horoscope-question-card__back">
                    <small>${escapeHtml(sign.name)} insight</small>
                    <strong>${escapeHtml(answer)}</strong>
                    <i>Back to question <span aria-hidden="true">↺</span></i>
                  </span>
                </span>
              </button>`).join('')}
          </div>
        </section>`;
    }

    renderCompatibilityGrid(periodKey, sign, signIndex) {
      if (this.dataset.showFullCompatibility !== 'true') return '';
      return `
        <section class="horoscope-compat-showcase horoscope-ornament" aria-labelledby="HoroscopeAllCompatibility-${escapeHtml(this.dataset.sectionId)}">
          <header class="horoscope-compat-showcase__heading">
            ${this.medallion(sign, 'horoscope-medallion--small')}
            <div>
              <h2 id="HoroscopeAllCompatibility-${escapeHtml(this.dataset.sectionId)}">${escapeHtml(sign.name)} Compatibility</h2>
              <p>Explore how ${escapeHtml(sign.name)} combines with every zodiac sign in communication, trust and shared values.</p>
            </div>
          </header>
          <div class="horoscope-compat-grid">
            ${this.data.signs.map((match, matchIndex) => {
              const score = this.compatibilityScore(sign, match, signIndex, matchIndex);
              return `<a class="horoscope-compat-card" href="${escapeHtml(this.url(periodKey, match.slug))}" data-horoscope-link>
                <span class="horoscope-compat-card__pair"><span>${escapeHtml(zodiacGlyph(sign.symbol))}</span><i aria-hidden="true">✦</i><span>${escapeHtml(zodiacGlyph(match.symbol))}</span></span>
                <strong>${escapeHtml(sign.name.slice(0, 3).toUpperCase())} &amp; ${escapeHtml(match.name.slice(0, 3).toUpperCase())}</strong>
                <b style="--compat-score:${score}">${score}%</b>
              </a>`;
            }).join('')}
          </div>
        </section>`;
    }

    renderPlanetary(periodKey, sign, reading) {
      if (this.dataset.showPlanetary !== 'true') return '';
      const recommendations = [
        [this.dataset.remedyOneTitle, this.dataset.remedyOneUrl],
        [this.dataset.remedyTwoTitle, this.dataset.remedyTwoUrl]
      ].filter(([title, url]) => title && url);
      return `
        <section class="horoscope-planetary-showcase horoscope-ornament" aria-labelledby="HoroscopePlanets-${escapeHtml(this.dataset.sectionId)}">
          <details class="horoscope-influence" open>
            <summary>
              <span class="horoscope-influence__icon" aria-hidden="true">☉</span>
              <span><strong id="HoroscopePlanets-${escapeHtml(this.dataset.sectionId)}">Planetary Influences</strong><small>What the planets are highlighting ${escapeHtml(this.data.periods[periodKey].label.toLowerCase())}</small></span>
              <i aria-hidden="true"></i>
            </summary>
            <div class="horoscope-influence__body">
              <div><strong>${escapeHtml(sign.ruler)} · Ruling planet</strong><span>Supports ${escapeHtml(sign.strength.toLowerCase())} and asks you to use personal power thoughtfully.</span></div>
              <div><strong>${escapeHtml(sign.element)} · Natural element</strong><span>Returns you to the pace, instincts and decision style that feel most authentic.</span></div>
              <div><strong>${escapeHtml(reading.focus)} · Current focus</strong><span>Encourages steady progress while bringing awareness to ${escapeHtml(reading.challenge)}.</span></div>
            </div>
          </details>
          ${this.dataset.showRemedy === 'true' ? `<div class="horoscope-remedy-line"><p><strong>Today’s remedy:</strong> ${escapeHtml(reading.remedy)}</p>${recommendations.length ? `<p>Inspired by today’s themes: ${recommendations.map(([title, url]) => `<a href="${escapeHtml(url)}">${escapeHtml(title)} <span aria-hidden="true">→</span></a>`).join('<i aria-hidden="true"></i>')}</p>` : ''}</div>` : ''}
        </section>`;
    }

    renderBestMatches(periodKey, sign) {
      if (this.dataset.showCompatibility !== 'true') return '';
      const matches = (sign.compatibility || []).map((slug) => this.data.signs.find((entry) => entry.slug === slug)).filter(Boolean).slice(0, 3);
      if (!matches.length) return '';
      return `<section class="horoscope-best-matches horoscope-ornament">
        <div class="horoscope-best-matches__seal" aria-hidden="true">♡</div>
        <div><h2>Best matches for ${escapeHtml(sign.name)}</h2><p>Explore the signs that can complement ${escapeHtml(sign.name)} in love, trust and shared values.</p>
          <div class="horoscope-match-pills">${matches.map((match) => `<a href="${escapeHtml(this.url(periodKey, match.slug))}" data-horoscope-link><span aria-hidden="true">${escapeHtml(zodiacGlyph(match.symbol))}</span>${escapeHtml(sign.name)} + ${escapeHtml(match.name)} <b aria-hidden="true">→</b></a>`).join('')}<a href="#HoroscopeAllCompatibility-${escapeHtml(this.dataset.sectionId)}"><span aria-hidden="true">⌘</span>All pairings</a></div>
        </div>
      </section>`;
    }

    renderDetail(periodKey, slug) {
      const signIndex = this.data.signs.findIndex((entry) => entry.slug === slug);
      const sign = this.data.signs[signIndex];
      const period = this.data.periods[periodKey];
      const reading = this.reading(periodKey, sign, signIndex);
      const previous = this.data.signs[(signIndex - 1 + this.data.signs.length) % this.data.signs.length];
      const next = this.data.signs[(signIndex + 1) % this.data.signs.length];
      const areas = [
        ['Career & Work', reading.career, 0],
        ['Health & Wellness', reading.health, -1],
        ['Love & Relationships', reading.love, periodKey === 'love' ? 1 : 0],
        ['Finance & Resources', reading.finance, -1]
      ];
      const intro = reading.intro || `${period.label} places ${reading.focus} at the centre of ${sign.name}’s attention. Your natural ${sign.strength.toLowerCase()} can move plans forward, provided you notice the tendency toward ${reading.challenge}. Choose the response that creates steady clarity instead of immediate relief.`;
      const headline = reading.headline || `${sign.name} ${period.label} Horoscope`;
      return `
        <article class="horoscope-detail">
          <nav class="horoscope-breadcrumb" aria-label="Breadcrumb">
            <a href="/">Home</a><span aria-hidden="true">/</span>
            <a href="${escapeHtml(this.url(periodKey))}" data-horoscope-link>Horoscope</a><span aria-hidden="true">/</span>
            <span aria-current="page">${escapeHtml(sign.name)}</span>
          </nav>

          <header class="horoscope-detail-hero horoscope-ornament">
            ${this.medallion(sign, 'horoscope-medallion--hero')}
            <div class="horoscope-detail-hero__copy">
              <p class="horoscope-detail__kicker">Updated ${escapeHtml(periodDateLabel(periodKey))} <span>${escapeHtml(sign.element)}</span></p>
              <h1>${escapeHtml(headline)}</h1>
              <div class="horoscope-detail__subline"><span>${escapeHtml(sign.dates)}</span><i aria-hidden="true">•</i><span>Ruled by ${escapeHtml(sign.ruler)}</span><i aria-hidden="true">•</i>${starMarkup(reading.score)}</div>
            </div>
            <span class="horoscope-detail-hero__constellation" aria-hidden="true">${escapeHtml(zodiacGlyph(sign.symbol))}</span>
          </header>

          <div class="horoscope-stat-strip" aria-label="Horoscope highlights">
            <div class="horoscope-stat"><span aria-hidden="true">☆</span><div><small>Today’s rating</small><strong>${starMarkup(reading.score)}</strong></div></div>
            <div class="horoscope-stat"><span class="horoscope-color-dot" style="--lucky-color:${escapeHtml(sign.color)}" aria-hidden="true"></span><div><small>Lucky color</small><strong>${escapeHtml(sign.color)}</strong></div></div>
            <div class="horoscope-stat"><span aria-hidden="true">${escapeHtml(sign.luckyNumber)}</span><div><small>Lucky number</small><strong>${escapeHtml(sign.luckyNumber)}</strong></div></div>
            <div class="horoscope-stat"><span aria-hidden="true">♄</span><div><small>Dominant planet</small><strong>${escapeHtml(sign.ruler)}</strong></div></div>
          </div>

          ${this.renderLifeQuestions(sign)}
          ${this.renderCompatibilityGrid(periodKey, sign, signIndex)}

          <section class="horoscope-daily-story horoscope-ornament" aria-labelledby="HoroscopeSummary-${escapeHtml(this.dataset.sectionId)}">
            <header class="horoscope-daily-story__heading"><span>${escapeHtml(this.tone(reading))}</span><h2 id="HoroscopeSummary-${escapeHtml(this.dataset.sectionId)}">${escapeHtml(reading.headline || `${sign.name}, let ${reading.focus.toLowerCase()} guide your next step.`)}</h2></header>
            <div class="horoscope-reading-chips">
              <span class="horoscope-reading-chip"><i aria-hidden="true">❧</i><span><strong>Today’s strength</strong>${escapeHtml(sign.strength)}</span></span>
              <span class="horoscope-reading-chip"><i aria-hidden="true">↗</i><span><strong>Growth area</strong>${escapeHtml(reading.challenge)}</span></span>
              <span class="horoscope-reading-chip"><i aria-hidden="true">☼</i><span><strong>Mood</strong>${escapeHtml(reading.mood)}</span></span>
            </div>
            <p class="horoscope-reading-summary"><span aria-hidden="true">✺</span>${escapeHtml(intro)}</p>
            <div class="horoscope-area-grid">
              ${areas.map(([title, text, offset], areaIndex) => `<section class="horoscope-area-card"><header class="horoscope-area-card__header"><span aria-hidden="true">${['▣', '❧', '♡', '▱'][areaIndex]}</span><h3>${escapeHtml(title)}</h3>${starMarkup(this.areaRating(reading.score, offset), `${title}: ${this.areaRating(reading.score, offset)} out of 5`)}</header><p>${escapeHtml(text)} Keep ${escapeHtml(reading.focus)} in mind as the practical theme for this period.</p></section>`).join('')}
            </div>
          </section>

          ${this.renderPlanetary(periodKey, sign, reading)}
          ${this.renderBestMatches(periodKey, sign)}

          <nav class="horoscope-sign-nav" aria-label="Browse zodiac signs">
            <a href="${escapeHtml(this.url(periodKey, previous.slug))}" data-horoscope-link>← ${escapeHtml(previous.name)}</a>
            <a href="${escapeHtml(this.url(periodKey))}" data-horoscope-link>All zodiac signs</a>
            <a href="${escapeHtml(this.url(periodKey, next.slug))}" data-horoscope-link>${escapeHtml(next.name)} →</a>
          </nav>

          ${this.disclaimer()}
        </article>`;
    }

    render() {
      if (!this.data) return;
      const state = this.state();
      this.renderPeriods(state.period, state.sign);
      this.view.innerHTML = state.sign ? this.renderDetail(state.period, state.sign) : this.renderOverview(state.period);
    }
  }

  if (!customElements.get('horoscope-experience')) {
    customElements.define('horoscope-experience', HoroscopeExperience);
  }
})();
